import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent,
  type SVGAttributes,
} from 'react';
import { hasFlag } from 'country-flag-icons';
import * as Flags from 'country-flag-icons/react/3x2';
import { cn } from '@/lib/cn';
import { useLookup } from '@/hooks/useLookups';

export interface CountrySelectProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
}

type FlagComponent = ComponentType<SVGAttributes<SVGSVGElement> & { title?: string }>;
const FLAG_BY_CODE = Flags as unknown as Record<string, FlagComponent | undefined>;

function FlagIcon({ code, className }: { code: string; className?: string }) {
  // The SVG flags ship as React components keyed by ISO-2 code. Some rows of
  // the `country` table have no corresponding flag asset, so `hasFlag` keeps
  // an unknown code rendering a neutral spacer rather than throwing.
  if (!hasFlag(code)) return null;
  const Component = FLAG_BY_CODE[code];
  if (!Component) return null;
  return (
    <Component
      title={code}
      className={cn('inline-block h-3.5 w-5 shrink-0 rounded-sm align-middle', className)}
    />
  );
}

/**
 * Dropdown of ISO-3166 alpha-2 country codes, loaded from the `country`
 * table through `/api/lookups/countries` rather than held as a list in the
 * source. A hand-kept list drifts from the database and cannot be made to
 * stop (#222, @Helder) — offering a code the table lacks gets the insert
 * refused by a foreign key. Value is always the
 * 2-letter code (e.g. "FR"); each option displays an SVG flag next to
 * the localised country name so the operator audience gets the same
 * glyph cross-platform (Windows has no native flag emoji and would
 * otherwise render a missing-character box).
 *
 * This is a custom listbox rather than a native `<select>` because
 * native `<option>` elements cannot host React components / SVGs.
 */
export const CountrySelect = forwardRef<HTMLButtonElement, CountrySelectProps>(
  (
    {
      value,
      onChange,
      placeholder: placeholderProp = 'Select country…',
      error,
      disabled,
      id: providedId,
      name,
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const errorId = `${id}-error`;
    const listboxId = `${id}-listbox`;
    const statusId = `${id}-status`;

    const { items: countries, loading, error: loadError } = useLookup('countries');

    const allOptions = useMemo(
      () =>
        countries.map((c) => ({
          code: c.code ?? '',
          name: c.label,
        })),
      [countries],
    );
    const labelByCode = useMemo(
      () => new Map(allOptions.map((c) => [c.code, c.name])),
      [allOptions],
    );

    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement | null>(null);
    const listboxRef = useRef<HTMLUListElement | null>(null);
    const searchRef = useRef<HTMLInputElement | null>(null);

    // What the operator types narrows the list on name or code (#155); the
    // keyboard then walks the narrowed list, not the full one.
    const options = useMemo(() => {
      const needle = query.trim().toLowerCase();
      if (!needle) return allOptions;
      return allOptions.filter((c) => `${c.name} ${c.code}`.toLowerCase().includes(needle));
    }, [query, allOptions]);

    const selectedIndex = useMemo(
      () => options.findIndex((c) => c.code === value),
      [options, value],
    );
    const selectedLabel = value ? labelByCode.get(value) : undefined;

    // Le menu refuse de s'ouvrir tant que le referentiel n'est pas la. Sans le
    // dire, le bouton a l'air normal et ne repond pas.
    const placeholder = loading ? 'Loading countries…' : placeholderProp;

    const closeMenu = useCallback(() => {
      setOpen(false);
      setActiveIndex(-1);
      setQuery('');
    }, []);

    const openMenu = useCallback(() => {
      if (disabled || loading) return;
      setOpen(true);
      setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }, [disabled, loading, selectedIndex]);

    useEffect(() => {
      if (open) searchRef.current?.focus();
    }, [open]);

    useEffect(() => {
      setActiveIndex(options.length > 0 ? 0 : -1);
    }, [options]);

    // If the consumer flips `disabled` to true while the menu is open,
    // collapse it so a now-disabled control cannot stay interactive.
    useEffect(() => {
      if (disabled && open) {
        closeMenu();
      }
    }, [disabled, open, closeMenu]);

    const choose = useCallback(
      (code: string) => {
        onChange?.(code);
        closeMenu();
      },
      [onChange, closeMenu],
    );

    // Click-outside to dismiss
    useEffect(() => {
      if (!open) return;
      const handler = (event: MouseEvent) => {
        if (!containerRef.current) return;
        if (event.target instanceof Node && !containerRef.current.contains(event.target)) {
          closeMenu();
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [open, closeMenu]);

    // Scroll the active option into view when it changes
    useEffect(() => {
      if (!open || activeIndex < 0 || !listboxRef.current) return;
      const child = listboxRef.current.children[activeIndex];
      if (child instanceof HTMLElement) {
        child.scrollIntoView({ block: 'nearest' });
      }
    }, [open, activeIndex]);

    const handleKeyDown = (
      event: KeyboardEvent<HTMLButtonElement | HTMLUListElement | HTMLInputElement>,
    ) => {
      if (disabled) return;
      const typing = event.currentTarget === searchRef.current;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!open) {
            openMenu();
          } else {
            setActiveIndex((prev) => Math.min(options.length - 1, prev + 1));
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!open) {
            openMenu();
          } else {
            setActiveIndex((prev) => Math.max(0, prev - 1));
          }
          break;
        case 'Home':
          if (open && !typing) {
            event.preventDefault();
            setActiveIndex(0);
          }
          break;
        case 'End':
          if (open && !typing) {
            event.preventDefault();
            setActiveIndex(options.length - 1);
          }
          break;
        case ' ':
          if (typing) break;
          event.preventDefault();
          if (open && activeIndex >= 0) {
            choose(options[activeIndex].code);
          } else {
            openMenu();
          }
          break;
        case 'Enter':
          event.preventDefault();
          if (open && activeIndex >= 0 && options[activeIndex]) {
            choose(options[activeIndex].code);
          } else if (!open) {
            openMenu();
          }
          break;
        case 'Escape':
          if (open) {
            event.preventDefault();
            closeMenu();
          }
          break;
        case 'Tab':
          if (open) closeMenu();
          break;
      }
    };

    return (
      <div ref={containerRef} className="relative w-full">
        <button
          ref={ref}
          type="button"
          id={id}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={cn(error && errorId, loading && statusId) || undefined}
          aria-busy={loading || undefined}
          disabled={disabled}
          onClick={() => (open ? closeMenu() : openMenu())}
          onKeyDown={handleKeyDown}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg border bg-white px-4 py-2.5 text-left text-sm outline-none transition-colors',
            'focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]',
            'disabled:cursor-not-allowed disabled:bg-[#f5f5f5] disabled:text-[#999]',
            error
              ? 'border-[#c62828] focus:border-[#c62828] focus:ring-[#c62828]'
              : 'border-[#e0e0e0]',
          )}
        >
          {value ? (
            <>
              <FlagIcon code={value} />
              <span className="truncate text-[#333]">
                {selectedLabel ? `${selectedLabel} (${value})` : value}
              </span>
            </>
          ) : (
            <span className="truncate text-[#999]">{placeholder}</span>
          )}
          <span aria-hidden="true" className="ml-auto text-[#999]">
            ▾
          </span>
        </button>

        {/*
          Le menu refuse de s'ouvrir tant que le referentiel charge. Le
          substitut ne le dit qu'a vide : un bouton qui porte deja un pays ne
          change pas de texte, et un chargement qui commence pendant que le
          bouton a le focus ne s'annonce pas. Cette region le dit a voix
          haute, dans tous les cas.
        */}
        <span id={statusId} role="status" className="sr-only">
          {loading ? 'Loading countries…' : ''}
        </span>
        {/* Mirror the value into a hidden input so consumers using a plain
            <form> with a `name` get the country code on submit, matching
            the native <select> semantics the previous implementation had.
            Skipped when disabled — native <select disabled> doesn't submit
            either, so a disabled CountrySelect shouldn't post stale values. */}
        {name && !disabled && <input type="hidden" name={name} value={value ?? ''} />}
        {open && (
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-[#e0e0e0] bg-white shadow-lg">
            <div className="border-b border-[#eee] p-2">
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type to filter…"
                role="combobox"
                aria-label="Filter countries"
                aria-autocomplete="list"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-activedescendant={
                  activeIndex >= 0 && options[activeIndex]
                    ? `${id}-option-${activeIndex}`
                    : undefined
                }
                className="w-full rounded-md border border-[#e0e0e0] px-3 py-1.5 text-sm outline-none focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
              />
            </div>
            <ul
              ref={listboxRef}
              id={listboxId}
              role="listbox"
              aria-label="Country"
              tabIndex={-1}
              onKeyDown={handleKeyDown}
              className="max-h-64 overflow-auto py-1"
            >
              {options.length === 0 && (
                // Un referentiel qui n'a pas pu etre charge et un filtre qui
                // ne trouve rien donnent tous deux une liste vide. Les
                // confondre laissait l'operateur chercher une faute de frappe
                // devant une panne.
                <li className="px-3 py-2 text-sm text-[#999]">
                  {loadError ? 'Could not load the country list' : 'No country matches'}
                </li>
              )}
              {options.map((c, index) => {
                const isSelected = c.code === value;
                const isActive = index === activeIndex;
                return (
                  <li
                    key={c.code}
                    id={`${id}-option-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={(event) => {
                      // Use mousedown so the parent button doesn't lose focus
                      // first (which would close the menu before click fires).
                      event.preventDefault();
                      choose(c.code);
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
                      isActive ? 'bg-[#f5e9eb] text-[#1a1a1a]' : 'text-[#333]',
                      isSelected && 'font-medium',
                    )}
                  >
                    <FlagIcon code={c.code} />
                    <span className="truncate">
                      {c.name} ({c.code})
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {error && (
          <p id={errorId} className="mt-1.5 text-xs text-[#c62828]" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);

CountrySelect.displayName = 'CountrySelect';
